<script lang="ts">
    import { connection } from "../lib/connection.svelte.ts";
    import { editor } from "../lib/editor.svelte.ts";
    import { room } from "../lib/room.svelte.ts";
    import FacultyPill from "./FacultyPill.svelte";
    import SkillPlaque from "./SkillPlaque.svelte";
    import VerbEditor from "./VerbEditor.svelte";

    const FACULTY_PLACEHOLDERS = ["Brains", "Brawn", "Reflex", "Sense", "Will", "Guts"]
        .map(name => ({ name, mastery: 0, target: 0 }));

    let faculties = $derived(room.characterSheet?.faculties ?? FACULTY_PLACEHOLDERS);
    let skills    = $derived(room.characterSheet?.skills    ?? []);
    let integrity = $derived(room.characterSheet?.integrity ?? 75);
    let sanity    = $derived(room.characterSheet?.sanity    ?? 60);
</script>

<aside class="room-panel">
    {#if editor.active}
        {#key editor.session?.presentationId}
            <VerbEditor />
        {/key}
    {:else}
    <div class="room-image-frame">
        {#if room.roomImage}
            <img src={room.roomImage} alt={room.title ?? ""} class="room-image"
                 onerror={() => room.clearRoomImage()} />
        {:else}
            <div class="room-image-placeholder"></div>
        {/if}
    </div>
    {/if}

    <div class="stats-box">
        <header class="stats-header">
            <span class="orn">◈</span>
            <span>INVESTIGATOR</span>
            <span class="orn">◈</span>
        </header>
        <div class="stats-rule"></div>
        <div class="stats-content">

            <!-- Name + vital bars row -->
            <div class="name-row">
                <div class="name-cell">
                    <span class="stat-label">NAME</span>
                    <span class="stat-value">{connection.playerName || "—"}</span>
                </div>
                <div class="vital-bars">
                    <div class="vital-bar">
                        <span class="vital-label">INTEGRITY</span>
                        <div class="bar-track">
                            <div class="bar-fill integrity-fill" style="width: {integrity}%"></div>
                        </div>
                    </div>
                    <div class="vital-bar">
                        <span class="vital-label">SANITY</span>
                        <div class="bar-track">
                            <div class="bar-fill sanity-fill" style="width: {sanity}%"></div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="sheet-rule"></div>

            <!-- Faculties + Skills columns -->
            <div class="sheet-columns">
                <div class="faculties-col">
                    <div class="col-title">Faculties</div>
                    <div class="faculty-list">
                        {#each faculties as fac (fac.name)}
                            <FacultyPill name={fac.name} mastery={fac.mastery} description={fac.description} />
                        {/each}
                    </div>
                </div>
                <div class="skills-col">
                    <div class="col-title">Skills</div>
                    <div class="skill-cloud">
                        {#each skills as skill (skill.name)}
                            <SkillPlaque
                                name={skill.name}
                                mastery={skill.mastery}
                                target={skill.target}
                                description={skill.description}
                                profession={skill.profession}
                            />
                        {/each}
                    </div>
                </div>
            </div>

        </div>
    </div>
</aside>

<style>
    .room-panel {
        flex: 1 1 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding-top: 0.5rem;
    }

    /* ── Room image thumbnail ─────────────────────────────────────────── */

    .room-image-frame {
        width: 100%;
        aspect-ratio: 16 / 9;
        border: 2px solid #6b5520;
        box-shadow:
            0 0 0 1px #1a1208,
            0 0 0 3px #4a3810,
            0 3px 14px rgba(0, 0, 0, 0.85);
        overflow: hidden;
        line-height: 0;
        background: #080604;
        flex-shrink: 0;
    }

    .room-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        filter: grayscale(0.55) sepia(0.25) brightness(0.8);
    }

    .room-image-placeholder {
        width: 100%;
        height: 100%;
        background:
            repeating-linear-gradient(
                45deg,
                transparent,
                transparent 8px,
                rgba(80, 60, 10, 0.06) 8px,
                rgba(80, 60, 10, 0.06) 9px
            );
    }

    /* ── Stats box ────────────────────────────────────────────────────── */

    .stats-box {
        position: relative;
        flex: 1 1 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
        background: rgba(5, 7, 4, 0.97);
        border: 2px solid #7a6228;
        box-shadow:
            0 0 0 1px #1a1408,
            0 0 0 4px #5a4218,
            0 0 0 5px #111008,
            inset 0 0 0 1px rgba(110, 85, 20, 0.18),
            inset 0 0 22px rgba(0, 0, 0, 0.85),
            0 0 18px rgba(80, 60, 10, 0.18);
        padding: 0.6rem 0.75rem 0.7rem;
        font-family: 'Courier New', Courier, monospace;
        overflow: hidden;
    }

    .stats-box::before {
        content: '';
        position: absolute;
        inset: 0;
        background: repeating-linear-gradient(
            0deg,
            rgba(0, 0, 0, 0) 0px,
            rgba(0, 0, 0, 0) 3px,
            rgba(0, 0, 0, 0.16) 3px,
            rgba(0, 0, 0, 0.16) 4px
        );
        pointer-events: none;
        z-index: 3;
    }

    .stats-box::after {
        content: '';
        position: absolute;
        inset: 5px;
        border: 1px solid rgba(110, 85, 22, 0.14);
        pointer-events: none;
        z-index: 2;
    }

    .stats-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 0.70rem;
        letter-spacing: 0.2em;
        color: #c19d5a;
        text-transform: uppercase;
        margin-bottom: 0.35rem;
        position: relative;
        z-index: 4;
        flex-shrink: 0;
    }

    .orn {
        color: #7e612d;
        font-size: 0.62rem;
        font-family: inherit;
    }

    .stats-rule {
        height: 1px;
        background: linear-gradient(
            90deg,
            transparent,
            #6b5228 15%,
            #8a6a32 50%,
            #6b5228 85%,
            transparent
        );
        margin-bottom: 0.5rem;
        position: relative;
        z-index: 4;
        flex-shrink: 0;
    }

    /* ── Stats content ────────────────────────────────────────────────── */

    .stats-content {
        position: relative;
        z-index: 4;
        flex: 1 1 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }

    /* ── Name row with vital bars ─────────────────────────────────────── */

    .name-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        flex-shrink: 0;
    }

    .name-cell {
        display: flex;
        align-items: baseline;
        gap: 0.4rem;
        min-width: 0;
    }

    .stat-label {
        font-size: 0.70rem;
        letter-spacing: 0.12em;
        color: #6a5830;
        text-transform: uppercase;
        flex-shrink: 0;
    }

    .stat-label::after { content: ':'; }

    .stat-value {
        font-size: 0.86rem;
        color: #c8a840;
        text-shadow: 0 0 7px rgba(200, 168, 64, 0.45);
        letter-spacing: 0.04em;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .vital-bars {
        display: flex;
        gap: 0.55rem;
        flex-shrink: 0;
    }

    .vital-bar {
        display: flex;
        flex-direction: column;
        gap: 0.12rem;
    }

    .vital-label {
        font-size: 0.55rem;
        letter-spacing: 0.1em;
        color: #6a5830;
        text-transform: uppercase;
    }

    .bar-track {
        width: 54px;
        height: 4px;
        background: rgba(0, 0, 0, 0.55);
        border: 1px solid rgba(90, 68, 15, 0.4);
        overflow: hidden;
    }

    .bar-fill {
        height: 100%;
        transition: width 0.5s ease;
    }

    .integrity-fill {
        background: linear-gradient(90deg, #6b1212, #c02828);
        box-shadow: 0 0 5px rgba(200, 40, 40, 0.55);
    }

    .sanity-fill {
        background: linear-gradient(90deg, #0e1f42, #1e48a0);
        box-shadow: 0 0 5px rgba(40, 90, 200, 0.55);
    }

    /* ── Sheet rule ───────────────────────────────────────────────────── */

    .sheet-rule {
        height: 1px;
        background: linear-gradient(
            90deg,
            transparent,
            rgba(107, 82, 40, 0.4) 15%,
            rgba(107, 82, 40, 0.4) 85%,
            transparent
        );
        margin: 0.45rem 0 0.35rem;
        flex-shrink: 0;
    }

    /* ── Two-column section ───────────────────────────────────────────── */

    .sheet-columns {
        flex: 1 1 0;
        min-height: 0;
        display: flex;
        gap: 0.6rem;
        align-items: stretch;
    }

    .faculties-col {
        flex: 0 0 42%;
        min-width: 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }

    .skills-col {
        flex: 1 1 0;
        min-width: 0;
        display: flex;
        flex-direction: column;
    }

    .col-title {
        font-size: 0.62rem;
        letter-spacing: 0.14em;
        color: #947b43;
        text-transform: uppercase;
        border-bottom: 1px solid rgba(107, 82, 40, 0.3);
        padding-bottom: 0.15rem;
        margin-bottom: 0.3rem;
        flex-shrink: 0;
    }

    /* ── Faculty list ─────────────────────────────────────────────────── */

    .faculty-list {
        flex: 1 1 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
        gap: 0.45rem;
        overflow-y: auto;
    }

    /* ── Skill plaques ────────────────────────────────────────────────── */

    .skill-cloud {
        display: flex;
        flex-wrap: wrap;
        gap: 0.3rem;
        align-content: flex-start;
    }
</style>
